import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { StartPage } from '../pages/StartPage';
import { authService, type AuthSession } from './session';

export type AuthStatus = 'restoring' | 'anonymous' | 'guest' | 'apple';

type AuthContextValue = {
  status: AuthStatus;
  session: AuthSession | null;
  busy: boolean;
  continueAsGuest(): Promise<void>;
  signInWithApple(): Promise<void>;
  refreshAccount(): Promise<void>;
  deleteAccount(): Promise<void>;
  logout(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const guestEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_GUEST === 'true';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('restoring');
  const [session, setSession] = useState<AuthSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    authService.restore()
      .then((restored) => {
        if (!active) return;
        setSession(restored);
        setStatus(restored?.provider ?? 'anonymous');
      })
      .catch(() => {
        if (!active) return;
        setStatus('anonymous');
        setError('시작 상태를 불러오지 못했습니다. 다시 시도해 주세요.');
      });
    return () => { active = false; };
  }, []);

  const runStart = useCallback(async (action: () => Promise<AuthSession>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await action();
      setSession(next);
      setStatus(next.provider);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '시작하지 못했습니다. 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }, []);

  const logout = useCallback(async () => {
    if (!session) return;
    setBusy(true);
    try {
      await authService.logout(session);
      setSession(null);
      setStatus('anonymous');
      setError(null);
    } finally {
      setBusy(false);
    }
  }, [session]);

  const refreshAccount = useCallback(async () => {
    if (session?.provider !== 'apple') return;
    const next = await authService.refreshAccount(session);
    setSession(next);
  }, [session]);

  const deleteAccount = useCallback(async () => {
    if (session?.provider !== 'apple') return;
    setBusy(true);
    try {
      await authService.deleteAccount(session);
      setSession(null);
      setStatus('anonymous');
      setError(null);
    } finally {
      setBusy(false);
    }
  }, [session]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    session,
    busy,
    continueAsGuest: () => runStart(authService.continueAsGuest),
    signInWithApple: () => runStart(authService.signInWithApple),
    refreshAccount,
    deleteAccount,
    logout,
  }), [busy, deleteAccount, logout, refreshAccount, runStart, session, status]);

  if (status === 'restoring') {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background px-6 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]" role="status">
        <div className="text-center">
          <img src="/daykeeper-logo-blue.png" alt="" className="mx-auto h-12 w-12 animate-pulse object-contain motion-reduce:animate-none" />
          <p className="mt-4 text-sm font-semibold text-muted-foreground">DayKeeper를 여는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {status === 'anonymous'
        ? <StartPage busy={busy} error={error} guestEnabled={guestEnabled} onApple={() => void value.signInWithApple()} onGuest={() => void value.continueAsGuest()} />
        : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
